import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '../../api/axios';
import {
  fetchAllProducts,
  createProduct as apiCreateProduct,
  updateProduct as apiUpdateProduct,
  deleteProduct as apiDeleteProduct,
  fetchAllOrders,
  createOrder as apiCreateOrder,
  updateOrderStatus as apiUpdateOrderStatus
} from '../../api/storeApi';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchAllProducts();
      setProducts(res.data);
    } catch (err) {
      console.error("Məhsullar yüklənərkən xəta:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const res = await fetchAllOrders();
      setOrders(res.data);
    } catch (err) {
      console.error("Sifarişlər yüklənərkən xəta:", err);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadOrders();

    const token = localStorage.getItem('adminToken');
    if (token) {
      setAdmin({ username: 'admin', role: 'admin' });
      setIsAuthenticated(true);
    }
  }, [loadProducts, loadOrders]);

  const login = async (username, password) => {
    try {
      const res = await API.post('/auth/login', { username, password });
      const { token } = res.data;
      localStorage.setItem('adminToken', token);
      setAdmin({ username, role: 'admin' });
      setIsAuthenticated(true);
      return true;
    } catch (err) {
      console.error("Giriş uğursuz:", err);
      return false;
    }
  };

  const logout = () => {
    setAdmin(null);
    setIsAuthenticated(false);
    localStorage.removeItem('adminToken');
  };

  const addProduct = async (productData) => {
    try {
      await apiCreateProduct(productData);
      await loadProducts();
    } catch (err) {
      console.error("Məhsul yaradıla bilmədi:", err);
      throw err;
    }
  };

  const updateProduct = async (id, productData) => {
    try {
      if (apiUpdateProduct) {
        await apiUpdateProduct(id, productData);
      }
      await loadProducts();
    } catch (err) {
      console.error("Məhsul yenilənə bilmədi:", err);
      throw err;
    }
  };

  const deleteProduct = async (id) => {
    try {
      await apiDeleteProduct(id);
      await loadProducts();
    } catch (err) {
      console.error("Məhsul silinə bilmədi:", err);
      throw err;
    }
  };

  const addOrder = async (orderPayload) => {
    try {
      const response = await apiCreateOrder(orderPayload);
      const savedData = response?.data || response;
      await Promise.all([loadProducts(), loadOrders()]);
      return savedData;
    } catch (err) {
      console.error("Sifariş yaradıla bilmədi:", err.response?.data || err.message);
      throw err;
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await apiUpdateOrderStatus(orderId, newStatus);
      await Promise.all([loadOrders(), loadProducts()]);
    } catch (err) {
      console.error("Status yenilənə bilmədi:", err);
      throw err;
    }
  };

  return (
    <AdminAuthContext.Provider value={{
      admin,
      isAuthenticated,
      login,
      logout,
      products,
      orders,
      loading,
      loadProducts,
      fetchProducts: loadProducts,
      addProduct,
      updateProduct,
      deleteProduct,
      addOrder,
      updateStatus: updateOrderStatus,
      updateOrderStatus
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;